import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Spinner,
  Dropdown,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSync } from "@fortawesome/free-solid-svg-icons";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import CustomTable from "../../components/CustomTable";
import CustomButton from "../../components/CustomButton";

import { useNavigate } from "react-router-dom";
import { Routes } from "../../routes";
import { useResponsive } from "../../hooks/useResponsive";
import ResponsiveContainer from "../../components/ResponsiveContainer";

function RetentionReport() {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    fetchApi("retention-report")
      .then((resp) => {
        if (resp.success && Array.isArray(resp.results)) {
          setData(resp.results);
        } else {
          setData([]);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = data.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(item.contractor_id).toLowerCase().includes(q) ||
      String(item.contract_no).toLowerCase().includes(q)
    );
  });

  const exportData = filtered.map((r, i) => ({
    "S/N": i + 1,
    "Contractor ID": r.contractor_id,
    "Contract No": r.contract_no,
    "Gross Amount": parseFloat(r.gross_amount || 0),
    "Total Retention": parseFloat(r.total_retention || 0),
  }));

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    FileSaver.saveAs(dataBlob, "retention-report.xlsx");
  };

  const exportToPdf = () => {
    const doc = new jsPDF();
    doc.text("Retention Report", 14, 15);
    autoTable(doc, {
      head: [["S/N", "Contractor ID", "Contract No", "Gross Amount", "Total Retention"]],
      body: exportData.map((r) => [
        r["S/N"],
        r["Contractor ID"],
        r["Contract No"],
        r["Gross Amount"],
        r["Total Retention"],
      ]),
      startY: 22,
    });
    doc.save("retention-report.pdf");
  };

  const fields = [
    {
      title: "Contractor ID",
      custom: true,
      className: "text-center",
      component: (item) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(
              `${Routes.expenditureControl.retentionReportView}/${item.contractor_id}/${item.contract_no}/${item.total_retention}`
            );
          }}
        >
          {item.contractor_id}
        </a>
      ),
    },
    {
      title: "Contract No",
      custom: true,
      className: "text-center",
      component: (item) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(
              `${Routes.expenditureControl.retentionReportView}/${item.contractor_id}/${item.contract_no}/${item.total_retention}`
            );
          }}
        >
          {item.contract_no}
        </a>
      ),
    },
    {
      title: "Gross Amount",
      custom: true,
      component: (item) => <span style={{ textAlign: "right", display: "block" }}>₦{formatNumber(item.gross_amount)}</span>,
    },
    {
      title: "Total Retention",
      custom: true,
      component: (item) => <span style={{ textAlign: "right", display: "block" }}>₦{formatNumber(item.total_retention)}</span>,
    },
  ];

  return (
    <ResponsiveContainer>
      <Card>
        <Card.Body>
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="mb-0">Retention Report</h5>
            </Col>
            <Col xs="auto">
              <Dropdown className="d-inline-block me-2">
                <Dropdown.Toggle variant="success" size="sm">
                  Export
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={exportToExcel}>Excel</Dropdown.Item>
                  <Dropdown.Item onClick={exportToPdf}>PDF</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <CustomButton variant="outline-primary" size="sm" onClick={fetchData}>
                <FontAwesomeIcon icon={faSync} className="me-1" />
                Refresh
              </CustomButton>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by Contractor ID or Contract No..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <CustomTable fields={fields} data={filtered} />
          )}
        </Card.Body>
      </Card>
    </ResponsiveContainer>
  );
}

export default RetentionReport;
